/**
 * 分支节点组件
 *
 * 递归显示单个分支及其子分支，支持：
 * - 展开/折叠子分支
 * - 分支状态标识（公开/当前分支）
 * - 进度统计显示
 * - 点击选择分支
 * - 拖拽排序功能
 */

import React, { useCallback, useState } from 'react';
import type { Branch, BranchTreeNode } from '../../types/branch';
import { useBranchStore } from '../../store/branchStore';

// ==================== SVG 图标 ====================

const ICONS = {
  // 展开图标
  chevronRight: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ),
  // 折叠图标
  chevronDown: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  ),
  // 公开分支图标
  publicBranch: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  // 主线图标
  mainLine: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  ),
  // 章节图标
  chapter: (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  // 字数图标
  word: (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  // 拖拽手柄图标
  dragHandle: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
    </svg>
  ),
};

// ==================== 子组件 ====================

/**
 * 进度统计显示组件
 */
interface ProgressStatsProps {
  chapterCount: number;
  wordCount: number;
}

const ProgressStats: React.FC<ProgressStatsProps> = ({ chapterCount, wordCount }) => {
  return (
    <div className="flex items-center gap-2 text-xs text-gray-600">
      <span className="flex items-center gap-1">
        {ICONS.chapter}
        {chapterCount}章
      </span>
      <span className="flex items-center gap-1">
        {ICONS.word}
        {wordCount.toLocaleString()}字
      </span>
    </div>
  );
};

/**
 * 分支状态标识组件
 */
interface BranchStatusBadgeProps {
  branch: Branch;
  isCurrentBranch: boolean;
}

const BranchStatusBadge: React.FC<BranchStatusBadgeProps> = ({ branch, isCurrentBranch }) => {
  if (isCurrentBranch) {
    return (
      <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
        当前
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
      {ICONS.publicBranch}
      公开
    </span>
  );
};

// ==================== 主组件 ====================

interface BranchNodeProps {
  /** 分支树节点数据 */
  node: BranchTreeNode;
  /** 是否为主线 */
  isMainLine?: boolean;
  /** 深度级别（用于缩进） */
  depth?: number;
  /** 点击分支回调 */
  onBranchClick?: (branch: Branch) => void;
  /** 切换分支回调 */
  onBranchSwitch?: (branchId: number | null) => void;
  /** 拖拽开始回调 */
  onDragStart?: (branch: Branch) => void;
  /** 拖拽结束回调 */
  onDragEnd?: () => void;
  /** 拖拽悬停回调 */
  onDragOver?: (branch: Branch, position: 'before' | 'after' | 'inside') => void;
  /** 放置回调 */
  onDrop?: (targetBranch: Branch, position: 'before' | 'after' | 'inside') => void;
  /** 是否正在拖拽中 */
  isDragging?: boolean;
  /** 拖拽目标信息 */
  dragTarget?: { branchId: number; position: 'before' | 'after' | 'inside' } | null;
}

export const BranchNode: React.FC<BranchNodeProps> = ({
  node,
  isMainLine = false,
  depth = 0,
  onBranchClick,
  onBranchSwitch,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragging = false,
  dragTarget = null,
}) => {
  const { branch } = node;
  const hasChildren = node.children && node.children.length > 0;

  // 从 store 获取状态
  const {
    expandedBranchIds,
    selectedBranchId,
    currentBranch,
    toggleBranchExpand,
    workspaceId,
    reorderBranches,
    branchTree,
  } = useBranchStore();

  const isExpanded = expandedBranchIds.has(branch.id);
  const isSelected = selectedBranchId === branch.id;
  const isCurrentBranch = currentBranch?.id === branch.id;

  // 本地拖拽状态
  const [localDragging, setLocalDragging] = useState(false);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | 'inside' | null>(null);

  // 处理展开/折叠
  const handleToggleExpand = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    toggleBranchExpand(branch.id);
  }, [branch.id, toggleBranchExpand]);

  // 处理点击
  const handleClick = useCallback(() => {
    onBranchClick?.(branch);
  }, [branch, onBranchClick]);

  // 处理双击切换
  const handleDoubleClick = useCallback(() => {
    onBranchSwitch?.(branch.id);
  }, [branch.id, onBranchSwitch]);

  // 拖拽开始
  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(branch.id));
    setLocalDragging(true);
    onDragStart?.(branch);
  }, [branch, onDragStart]);

  // 拖拽结束
  const handleDragEnd = useCallback(() => {
    setLocalDragging(false);
    setDropPosition(null);
    onDragEnd?.();
  }, [onDragEnd]);

  // 拖拽经过
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // 计算放置位置
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;

    let position: 'before' | 'after' | 'inside';
    if (y < height * 0.25) {
      position = 'before';
    } else if (y > height * 0.75) {
      position = 'after';
    } else {
      position = 'inside';
    }

    setDropPosition(position);
    onDragOver?.(branch, position);
  }, [branch, onDragOver]);

  // 拖拽离开
  const handleDragLeave = useCallback(() => {
    setDropPosition(null);
  }, []);

  // 放置
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const draggedBranchId = parseInt(e.dataTransfer.getData('text/plain'), 10);

    if (draggedBranchId === branch.id) {
      setDropPosition(null);
      return;
    }

    // 计算最终放置位置
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;

    let position: 'before' | 'after' | 'inside';
    if (y < height * 0.25) {
      position = 'before';
    } else if (y > height * 0.75) {
      position = 'after';
    } else {
      position = 'inside';
    }

    onDrop?.(branch, position);
    setDropPosition(null);
  }, [branch, onDrop]);

  // 计算缩进
  const paddingLeft = depth * 16;

  // 判断是否为拖拽目标
  const isDragTarget = dragTarget?.branchId === branch.id;

  // 收集同级分支用于排序
  const getSiblingsWithSelf = useCallback(() => {
    const collectSiblings = (nodes: BranchTreeNode[], targetId: number, parent: BranchTreeNode | null = null): { siblings: BranchTreeNode[], index: number, parent: BranchTreeNode | null } | null => {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].branch.id === targetId) {
          return { siblings: nodes, index: i, parent };
        }
        const found = collectSiblings(nodes[i].children, targetId, nodes[i]);
        if (found) return found;
      }
      return null;
    };
    return collectSiblings(branchTree, branch.id);
  }, [branchTree, branch.id]);

  // 处理拖拽排序
  const handleReorderDrop = useCallback(async (
    draggedBranchId: number,
    targetBranch: Branch,
    position: 'before' | 'after' | 'inside'
  ) => {
    if (!workspaceId) return;

    // 获取目标分支的同级分支
    const result = getSiblingsWithSelf();
    if (!result) return;

    const { siblings } = result;

    // 如果是 inside，将拖拽的分支作为目标分支的子分支
    if (position === 'inside') {
      // 暂不支持嵌套，只支持同级排序
      return;
    }

    // 创建新的排序列表
    const newOrder: Array<{ id: number; sort_order: number }> = [];
    let orderIndex = 0;

    // 找到拖拽分支在同级中的位置
    const draggedIndex = siblings.findIndex(n => n.branch.id === draggedBranchId);
    if (draggedIndex === -1) return;

    const targetIndex = siblings.findIndex(n => n.branch.id === targetBranch.id);
    if (targetIndex === -1) return;

    // 重新排序
    for (let i = 0; i < siblings.length; i++) {
      if (i === draggedIndex) continue; // 跳过拖拽的分支

      if (i === targetIndex) {
        if (position === 'before') {
          newOrder.push({ id: draggedBranchId, sort_order: orderIndex++ });
          newOrder.push({ id: siblings[i].branch.id, sort_order: orderIndex++ });
        } else {
          newOrder.push({ id: siblings[i].branch.id, sort_order: orderIndex++ });
          newOrder.push({ id: draggedBranchId, sort_order: orderIndex++ });
        }
      } else {
        newOrder.push({ id: siblings[i].branch.id, sort_order: orderIndex++ });
      }
    }

    // 如果目标分支不在列表中（在拖拽分支之后），添加到最后
    if (draggedIndex < targetIndex && !newOrder.find(o => o.id === draggedBranchId)) {
      newOrder.push({ id: draggedBranchId, sort_order: orderIndex });
    }

    // 发送排序更新
    await reorderBranches(workspaceId, newOrder);
  }, [workspaceId, reorderBranches, getSiblingsWithSelf]);

  // 计算拖拽放置指示器样式
  const getDropIndicatorStyle = () => {
    if (!dropPosition) return {};
    if (dropPosition === 'before') {
      return { borderTop: '2px solid #3b82f6' };
    }
    if (dropPosition === 'after') {
      return { borderBottom: '2px solid #3b82f6' };
    }
    if (dropPosition === 'inside') {
      return { backgroundColor: 'rgba(59, 130, 246, 0.1)' };
    }
    return {};
  };

  return (
    <div className="select-none">
      {/* 分支节点 */}
      <div
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          flex items-center gap-2 py-2 px-3 rounded-lg cursor-pointer
          transition-all duration-150 group
          ${isSelected
            ? 'bg-blue-50 border-l-4 border-blue-500'
            : 'hover:bg-gray-50 border-l-4 border-transparent'
          }
          ${isCurrentBranch ? 'ring-2 ring-blue-200' : ''}
          ${localDragging ? 'opacity-50 bg-blue-50' : ''}
          ${isDragTarget && dragTarget?.position === 'inside' ? 'bg-blue-100' : ''}
        `}
        style={{
          paddingLeft: paddingLeft + 12,
          ...(isDragTarget ? getDropIndicatorStyle() : {}),
        }}
      >
        {/* 拖拽手柄 */}
        <div
          className="flex-shrink-0 cursor-grab text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
          title="拖拽排序"
        >
          {ICONS.dragHandle}
        </div>

        {/* 展开/折叠按钮 */}
        <div
          className={`flex-shrink-0 ${hasChildren ? 'cursor-pointer' : 'invisible'}`}
          onClick={handleToggleExpand}
        >
          {hasChildren ? (
            isExpanded ? ICONS.chevronDown : ICONS.chevronRight
          ) : (
            <span className="w-4 h-4" />
          )}
        </div>

        {/* 分支图标 */}
        <div className="flex-shrink-0 text-gray-500">
          {isMainLine ? ICONS.mainLine : ICONS.publicBranch}
        </div>

        {/* 分支名称 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-medium truncate ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
              {branch.name}
            </span>
            <BranchStatusBadge branch={branch} isCurrentBranch={isCurrentBranch} />
          </div>
          {branch.description && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {branch.description}
            </p>
          )}
        </div>

        {/* 进度统计 */}
        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <ProgressStats
            chapterCount={node.chapter_count}
            wordCount={node.word_count}
          />
        </div>
      </div>

      {/* 拖拽放置指示线 */}
      {dropPosition === 'before' && (
        <div
          className="h-0.5 bg-blue-500 mx-3"
          style={{ marginLeft: paddingLeft + 12 }}
        />
      )}

      {/* 子分支（递归渲染） */}
      {hasChildren && isExpanded && (
        <div className="border-l-2 border-gray-100 ml-6">
          {node.children.map((childNode) => (
            <BranchNode
              key={childNode.branch.id}
              node={childNode}
              depth={depth + 1}
              onBranchClick={onBranchClick}
              onBranchSwitch={onBranchSwitch}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onDrop={onDrop}
              isDragging={isDragging}
              dragTarget={dragTarget}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BranchNode;